import { Inject, Component, Input, OnDestroy, OnInit, ChangeDetectorRef, DOCUMENT } from '@angular/core';

import { BehaviorSubject, combineLatest, Subject } from 'rxjs';
import { filter, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { Dataset } from 'src/app/modules/core/store/models/dataset.model';
import { Document, DocumentSchema } from 'src/app/modules/core/store/models/document.model';
import { DocumentReader } from 'src/app/modules/core/store/models/document-reader';
import * as fromSharing from 'src/app/modules/sharing/store';
import {
  Communication,
  CommunicationStatus,
  ConfiguredChannel,
  EverbridgeTemplate,
  ChannelUIds,
  ChannelType,
  MassCommsContactWithDistance,
  MassCommsContactDataset,
  MassCommsContact
} from 'src/app/modules/management/store/communication-center';
import { TemplateControlService } from 'src/app/modules/core/components/template-builder/controls/template-control.service';
import { DocumentSummaryWithDistance } from 'src/app/modules/cop/store/impacted-assets/impacted-assets.model';
import { NgxSpinnerService } from 'ngx-spinner';
import * as fromUserProfile from 'src/app/modules/core/store/user-profile';
import { DatasetApiService } from 'src/app/modules/management/store/datasets/dataset-api.service';
import { SystemNotificationsService } from '@app/core/system-notifications/system-notifications.service';

@Component({
    selector: 'topo-share-document-everbridge-dialog',
    templateUrl: './share-document-everbridge-dialog.component.html',
    styleUrls: ['./share-document-everbridge-dialog.component.scss'],
    standalone: false
})
export class ShareDocumentEverbridgeDialogComponent implements OnInit, OnDestroy {
  @Input() isActive: boolean;
  @Input() document: Document;
  @Input() dataset: Dataset;
  @Input() distanceDropdownValue: any;
  @Input() pathToMap: string;

  communicationMeta$: BehaviorSubject<any> = new BehaviorSubject(null);
  @Input() set communicationMeta(value: any) {
    this.communicationMeta$.next(value);
  }

  @Input() isClosestAssetWorking: boolean;
  @Input() set selectedDocumentAssetsByDistance(data: any[]) {
    var assetDocs = [] as DocumentSummaryWithDistance[];

    this._selectedDocumentAssetsByDistance = data;

    data.forEach(a => {
      assetDocs = assetDocs.concat(a.summariesWithDistance);
    });

    this.assetDocuments = assetDocs;
    this.contactDatasets.map(c => this.loadDatasetSchemaAndContacts(c.contactsDatasetUId));
  }
  _selectedDocumentAssetsByDistance: any[];

  recipientsInput$ = new Subject<string>();
  destroy$: Subject<void> = new Subject<void>();
  recipientsLoading = false;
  toSelectedRecipients: any[] = [];

  selectedChannel: ConfiguredChannel;
  selectedEverbridgeTemplate: EverbridgeTemplate;
  selectedEverbridgeTemplatePreview: EverbridgeTemplate;

  assets: string;
  mapImage: string;
  assetDocuments: DocumentSummaryWithDistance[] = [];
  assetContactsForDisplay: MassCommsContactWithDistance[] = [];
  tabs: string[] = [];
  selectedTabIndex = 0;
  selectedContacts: MassCommsContact[] = [];
  isAllChecked = true;
  distanceValue: any = 50;
  skeletonLoad: boolean;
  assetNameLabel: string;
  distanceLabel: string;
  channelType: ChannelType = ChannelType.MassNotifications;
  datasetSchemas: DocumentSchema[] = [];
  contactDatasets: MassCommsContactDataset[] = [];
  distListGroupName: string;
  contactGroupName: string;
  isDrawn = false;
  distanceImperialOptions = [{ value: 5 }, { value: 10 }, { value: 25 }, { value: 50 }];
  distanceMetricOptions = [{ value: 10 }, { value: 15 }, { value: 40 }, { value: 80 }];
  distanceOptions = this.distanceImperialOptions;
  shortDistanceUnit: string;
  longDistanceUnit: string;
  sortExpression: any;

  constructor(
    @Inject(DOCUMENT) private domDocument: any,
    private store: Store<any>,
    private translate: TranslateService,
    private notify: SystemNotificationsService,
    public templateControlService: TemplateControlService,
    private datasetApiService: DatasetApiService,
    private spinnerService: NgxSpinnerService,
    private changeDetector: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.tabs.push(this.translate.instant('MAP.MESSAGE-TAB'));
    this.tabs.push(this.translate.instant('MAP.RECIPIENTS-TAB'));
    this.assetNameLabel = this.translate.instant('MAP.ASSET-NAME');
    this.distanceLabel = this.translate.instant('MAP.DISTANCE-FROM-ALERT');

    this.sortExpression = null;

    this.skeletonLoad = this.isClosestAssetWorking;
    this.isClosestAssetWorking ? this.spinnerService.show() : this.spinnerService.hide();

    var assetDocs = [] as DocumentSummaryWithDistance[];

    this._selectedDocumentAssetsByDistance.forEach(a => {
      assetDocs = assetDocs.concat(a.summariesWithDistance);
    });

    this.assetDocuments = assetDocs;
    this.contactDatasets.map(c => this.loadDatasetSchemaAndContacts(c.contactsDatasetUId));

    this.store
      .select(fromSharing.selectSelectedChannel)
      .pipe(withLatestFrom(this.store.select(fromSharing.selectSelectedEverbridgeTemplate)), takeUntil(this.destroy$))
      .subscribe(([selectedChannel, selectedEverbridgeTemplate]) => {
        this.selectedChannel = selectedChannel;
        this.selectedEverbridgeTemplate = selectedEverbridgeTemplate;
        this.selectedEverbridgeTemplatePreview = null;
        this.toSelectedRecipients = [];

        if (!selectedChannel || !selectedEverbridgeTemplate) {
          return;
        }

        this.getTemplatePreview();

        if (selectedChannel?.settings?.contactDatasets) {
          this.contactDatasets = selectedChannel.settings.contactDatasets;
        } else {
          // For channels created before the contactDatasets array
          this.contactDatasets = [
            {
              contactsDatasetUId: selectedChannel?.settings?.contactsDatasetUId,
              contactIdFieldUId: selectedChannel?.settings?.contactIdFieldUId
            }
          ];
        }
        this.contactDatasets.map(c => this.loadDatasetSchemaAndContacts(c.contactsDatasetUId));
      });

    combineLatest([
      this.communicationMeta$,
      this.store.select(fromUserProfile.selectDistanceUnit),
      this.store.select(fromUserProfile.selectOrganization)
    ])
      .pipe(
        takeUntil(this.destroy$),
        filter(([commMeta]) => {
          return !!commMeta;
        }),
        tap(([commMeta, unit, organization]) => {
          this.isDrawn = commMeta?.isDrawn;

          const isImperialUnit = 'miles'.includes(unit?.toLocaleLowerCase());
          if (organization?.mapSettings) {
            this.distanceMetricOptions =
              organization.mapSettings.assetsImpactedDistanceOptionsInMetricUnits?.map(value => ({ value })) ?? this.distanceMetricOptions;
            this.distanceImperialOptions =
              organization.mapSettings.assetsImpactedDistanceOptionsInImperialUnits?.map(value => ({ value })) ??
              this.distanceImperialOptions;
          }

          if (!this.distanceDropdownValue) {
            if (isImperialUnit) {
              if (commMeta?.metersWithin && !Number.isNaN(commMeta?.metersWithin) && commMeta?.metersWithin === 0) {
                this.distanceValue = 50;
              } else {
                this.distanceValue = organization?.mapSettings?.assetsImpactedDistanceDefaultInImperialUnits;
              }
            } else {
              if (commMeta?.metersWithin && !Number.isNaN(commMeta?.metersWithin) && commMeta?.metersWithin === 0) {
                this.distanceValue = 100;
              } else {
                this.distanceValue = organization?.mapSettings?.assetsImpactedDistanceDefaultInMetricUnits;
              }
            }
          } else {
            this.distanceValue = this.distanceDropdownValue;

            if (isImperialUnit && !this.distanceImperialOptions.find(o => o.value === this.distanceDropdownValue)) {
              this.distanceImperialOptions.push({ value: this.distanceDropdownValue });
            } else if (!isImperialUnit && !this.distanceMetricOptions.find(o => o.value === this.distanceDropdownValue)) {
              this.distanceMetricOptions.push({ value: this.distanceDropdownValue });
            }
          }

          this.shortDistanceUnit = isImperialUnit
            ? this.translate.instant('UNITS.DISTANCE.MILES-ABBREVIATED-LOWER')
            : this.translate.instant('UNITS.DISTANCE.KILOMETERS-ABBREVIATED-LOWER');
          this.longDistanceUnit = isImperialUnit
            ? this.translate.instant('UNITS.DISTANCE.MILES-CAMEL')
            : this.translate.instant('UNITS.DISTANCE.KILOMETERS-CAMEL');
          this.distanceOptions = (isImperialUnit ? this.distanceImperialOptions : this.distanceMetricOptions).sort((a, b) =>
            a.value > b.value ? 1 : -1
          );
        })
      )
      .subscribe();

    this.translate.get(['MAP.DISTRIBUTION-LISTS', 'MAP.CONTACTS']).subscribe(values => {
      this.distListGroupName = values['MAP.DISTRIBUTION-LISTS'];
      this.contactGroupName = values['MAP.CONTACTS'];
    });
  }

  getTemplatePreview(): void {
    this.store.dispatch(fromSharing.replaceEverbridgeTokensFromApi({ template: this.selectedEverbridgeTemplate, document: this.document }));

    this.store
      .select(fromSharing.selectSelectedEverbridgeTemplatePreview)
      .pipe(takeUntil(this.destroy$))
      .subscribe(template => {
        if (template) {
          this.selectedEverbridgeTemplatePreview = { ...template };
        } else {
          this.selectedEverbridgeTemplatePreview = { ...this.selectedEverbridgeTemplate };
        }
      });
  }

  tabChanged(index: number): void {
    this.selectedTabIndex = index;
  }

  loadDatasetSchemaAndContacts(datasetUId: string) {
    return new Promise(resolve => {
      this.datasetApiService.getDatasetSchema(datasetUId).subscribe(res => {
        this.datasetSchemas[datasetUId] = res;
        // Parse contacts from the incoming asset documents only after dataset schema is loaded
        this.loadContactsFromDocuments(this.assetDocuments, datasetUId);
        this.changeDetector.markForCheck();
        setTimeout(() => {
          this.selectAll(true);
        }, 0);
      });
    });
  }

  loadContactsFromDocuments(documents: DocumentSummaryWithDistance[], datasetUId: string): MassCommsContactWithDistance[] {
    this.assetContactsForDisplay = [];

    if (this.selectedChannel?.channelUId != ChannelUIds.EverbridgeChannelUId) {
      return;
    }

    var contactAssets = documents.filter(d => d.layer.datasetUId === datasetUId);

    var contacts = contactAssets.map(d => this.mapDocumentToContact(d));

    this.assetContactsForDisplay.push(...contacts.filter(c => !!c));
  }

  mapDocumentToContact(document: DocumentSummaryWithDistance): MassCommsContactWithDistance {
    const reader = new DocumentReader(document.document);
    const datasetUid = document.layer.datasetUId;

    const schema: DocumentSchema = this.datasetSchemas[datasetUid];

    const contactIdFieldUId = this.contactDatasets.find(c => c.contactsDatasetUId === datasetUid).contactIdFieldUId;
    const contactIdField = schema?.fields?.find(f => f.uId === contactIdFieldUId);

    const name = this.getContactName(reader);
    const contactId = reader.getFieldValue(contactIdField?.systemName);

    if (!contactId) {
      return null;
    }

    return {
      name: name,
      contactId: contactId,
      distance: document.distance,
      distanceText: document.distanceText
    };
  }

  onChangeAssetDistance() {
    const newAssetsForDisplay = this.assetDocuments.filter(c => c.distance <= this.distanceValue);

    var newContactsForDisplay: MassCommsContactWithDistance[] = [];
    const oldContactsForDisplay = [...this.assetContactsForDisplay];

    this.contactDatasets?.map(c => {
      var contactAssets = newAssetsForDisplay.filter(d => d.layer.datasetUId === c.contactsDatasetUId);

      var contacts = contactAssets.map(d => this.mapDocumentToContact(d));

      newContactsForDisplay.push(...contacts);
    });

    const valuesAdded = newContactsForDisplay.filter(x => !oldContactsForDisplay.find(d => d.contactId === x.contactId));
    valuesAdded.forEach(item => {
      this.assetContactsForDisplay.push(item);
    });

    const valuesRemoved = oldContactsForDisplay.filter(x => !newContactsForDisplay.find(d => d.contactId === x.contactId));
    valuesRemoved.forEach(item => {
      this.selectedContacts = this.selectedContacts.filter(e => e.contactId !== item.contactId);
      this.assetContactsForDisplay.splice(
        this.assetContactsForDisplay.findIndex(d => d.contactId === item.contactId),
        1
      );
    });

    if (this.sortExpression) {
      this.onSort(this.sortExpression);
    }

    this.isAllChecked = this.selectedContacts.length === this.assetContactsForDisplay.length;
  }

  onIsActiveClick(event, item: MassCommsContactWithDistance) {
    if (event.target.checked) {
      this.selectedContacts.push(item);
    } else {
      let filteredList = this.selectedContacts.filter(e => e.contactId !== item.contactId);
      this.selectedContacts = filteredList;
    }

    this.isAllChecked = this.selectedContacts.length === this.assetContactsForDisplay.length;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  close(): void {
    this.store.dispatch(fromSharing.hideShareDocumentDialog());
  }

  sendClick(): void {
    const reader = new DocumentReader(this.document);
    const documentUId = reader.uId;

    if (this.toSelectedRecipients?.length === 0 && this.selectedContacts?.length === 0) {
      this.translate
        .get('MAP.ERROR-COMMUNICATION-RECIPIENTS-REQUIRED')
        .subscribe(val => this.notify.error(val));
      return;
    }

    const toCustomRecipients = [];
    const toDistributionListUIds = [];
    const toMassCommsRecipients: MassCommsContact[] = [];

    for (const recipient of this.toSelectedRecipients) {
      if (recipient.type === this.contactGroupName) {
        let contact = { contactId: recipient.contactId, name: recipient.displayName } as MassCommsContact;
        toMassCommsRecipients.push(contact);
      } else if (recipient.type === this.distListGroupName) {
        toDistributionListUIds.push(recipient.uId);
      }
    }

    for (const contact of this.selectedContacts) {
      toCustomRecipients.push(contact.contactId);
    }

    const communication: Communication = {
      uId: null,
      name: null,
      description: null,
      configuredChannelUId: this.selectedChannel.uId,
      communicationTemplateUId: null,
      datasetUId: this.dataset.uId,
      documentUId,
      documentChangeUId: null,
      customSubject: this.selectedEverbridgeTemplatePreview?.title,
      customHtmlContent: this.selectedEverbridgeTemplatePreview?.textMessage,
      customTextContent: this.selectedEverbridgeTemplatePreview?.textSmsMessage,
      customDocument: null,
      toDistributionListUIds,
      ccDistributionListUIds: [],
      bccDistributionListUIds: [],
      toUserUIds: [],
      ccUserUIds: [],
      bccUserUIds: [],
      toCustomRecipients,
      ccCustomRecipients: [],
      bccCustomRecipients: [],
      status: CommunicationStatus.None,
      communicationStartTime: null,
      communicationCompletionTime: null,
      renderedTemplate: null,
      sendAsGroup: false,
      settings: { everbridgeTemplateId: this.selectedEverbridgeTemplate.id },
      toMassCommsRecipients: toMassCommsRecipients,
      sendDocumentAttachments: false,
      pathToMap: this.pathToMap
    };

    this.store.dispatch(fromSharing.sendCommunicationWithApi({ communication }));
    this.store.dispatch(fromSharing.hideShareDocumentDialog());
  }

  cancelClick(): void {
    this.store.dispatch(fromSharing.hideShareDocumentDialog());
  }

  contentScrolled(): void {
    this.templateControlService.hideSettingsControlIfNotEdit();
  }

  onSort(sortExpression: { sortField: string; sortDirection: string }): void {
    var arrayForSort = [...this.assetContactsForDisplay];

    this.sortExpression = sortExpression;

    if (sortExpression.sortDirection === 'Ascending') {
      arrayForSort.sort((a, b) =>
        a[sortExpression.sortField.toLocaleLowerCase()] < b[sortExpression.sortField.toLocaleLowerCase()] ? 1 : -1
      );
    } else {
      arrayForSort.sort((a, b) =>
        a[sortExpression.sortField.toLocaleLowerCase()] > b[sortExpression.sortField.toLocaleLowerCase()] ? 1 : -1
      );
    }

    this.assetContactsForDisplay = arrayForSort;
  }

  selectAll(event: any): void {
    this.selectedContacts = [];

    this.assetContactsForDisplay.forEach(item => {
      var ele = document.getElementById(item.contactId) as HTMLInputElement;

      if (ele) {
        ele.checked = event;
        if (event) {
          this.selectedContacts.push(item);
        }
      }
    });

    this.isAllChecked = this.assetContactsForDisplay?.length === this.selectedContacts?.length;
  }

  getContactName(reader: DocumentReader): string {
    var name = '';

    const first = reader.getFieldValue('first_name') ?? reader.getFieldValue('first');
    const last = reader.getFieldValue('last_name') ?? reader.getFieldValue('last');

    if (first && last) {
      name = first + ' ' + last;
    } else if (first) {
      name = first;
    } else if (last) {
      name = last;
    } else {
      name = reader.name;
    }

    return name;
  }
}
